// Stefans-AI-CV-frontend/src/components/Navbar.tsx

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAppStore } from '../stores/appStore'
import { useI18n } from '../i18n'
import { analyticsService } from '../services/analytics'

const navLinks = [
  { key: 'about', id: 'about' },
  { key: 'projects', id: 'projects' },
  { key: 'research', id: 'research' },
  { key: 'experience', id: 'experience' },
  { key: 'chat', id: 'chat' },
]

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const { currentLanguage, setLanguage } = useAppStore()
  const t = useI18n()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToSection = (id: string) => {
    analyticsService.trackEvent('nav_click', {
      category: 'navigation',
      label: id,
      section: id,
    })
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    }
    setMobileOpen(false)
  }

  const handleLanguageChange = (language: 'zh' | 'en') => {
    analyticsService.trackEvent('language_change', {
      category: 'preference',
      label: language,
      language,
    })
    setLanguage(language)
  }

  const navLabel = (key: string) => {
    const map: Record<string, string> = {
      about: t.nav.about,
      chat: t.nav.chat,
      experience: t.nav.experience,
      projects: t.nav.projects,
      research: t.nav.research,
      contact: t.nav.contact,
    }
    return map[key] || key
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-bg-base/90 backdrop-blur-md border-b border-border'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <button
            onClick={() => {
              analyticsService.trackEvent('logo_click', {
                category: 'navigation',
                label: 'home',
              })
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
            className="flex items-center gap-2 group"
          >
            <span className="font-heading font-700 text-xl text-text-primary group-hover:text-cyan-400 transition-colors">
              S<span className="text-cyan-400">.</span>
            </span>
          </button>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.key}
                onClick={() => scrollToSection(link.id)}
                className="px-3 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors rounded-lg hover:bg-white/5"
              >
                {navLabel(link.key)}
              </button>
            ))}
          </nav>

          {/* Right: Language toggle + mobile menu */}
          <div className="flex items-center gap-3">
            {/* Language toggle */}
            <div className="flex items-center gap-0.5 bg-white/5 rounded-lg p-0.5 border border-border">
              <button
                onClick={() => handleLanguageChange('zh')}
                className={`px-2.5 py-1 text-xs rounded-md transition-all font-medium ${
                  currentLanguage === 'zh'
                    ? 'bg-cyan-400/20 text-cyan-400'
                    : 'text-text-muted hover:text-text-secondary'
                }`}
              >
                CN
              </button>
              <button
                onClick={() => handleLanguageChange('en')}
                className={`px-2.5 py-1 text-xs rounded-md transition-all font-medium ${
                  currentLanguage === 'en'
                    ? 'bg-cyan-400/20 text-cyan-400'
                    : 'text-text-muted hover:text-text-secondary'
                }`}
              >
                EN
              </button>
            </div>

            {/* Mobile hamburger */}
            <button
              className="md:hidden flex flex-col gap-1.5 p-2 rounded-lg hover:bg-white/5 transition-colors"
              onClick={() => {
                analyticsService.trackEvent('mobile_menu_toggle', {
                  category: 'navigation',
                  label: mobileOpen ? 'close' : 'open',
                  is_opening: !mobileOpen,
                })
                setMobileOpen(!mobileOpen)
              }}
              aria-label="Toggle menu"
            >
              <span
                className={`block w-5 h-0.5 bg-text-secondary transition-all duration-300 ${
                  mobileOpen ? 'rotate-45 translate-y-2' : ''
                }`}
              />
              <span
                className={`block w-5 h-0.5 bg-text-secondary transition-all duration-300 ${
                  mobileOpen ? 'opacity-0' : ''
                }`}
              />
              <span
                className={`block w-5 h-0.5 bg-text-secondary transition-all duration-300 ${
                  mobileOpen ? '-rotate-45 -translate-y-2' : ''
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile slide-down panel */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-bg-elevated/95 backdrop-blur-md border-b border-border overflow-hidden"
          >
            <nav className="flex flex-col px-4 py-3 gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.key}
                  onClick={() => scrollToSection(link.id)}
                  className="text-left px-3 py-3 text-sm text-text-secondary hover:text-text-primary hover:bg-white/5 rounded-lg transition-colors"
                >
                  {navLabel(link.key)}
                </button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
